import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { contactFormData } from '../data/mock';
import { toast } from 'sonner';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    medication: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast.success('Request submitted successfully! Our team will contact you soon.');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        country: '',
        medication: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-red-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              {contactFormData.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {contactFormData.subtitle}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="grid md:grid-cols-5">
              {/* Left Side - Info */}
              <div className="md:col-span-2 bg-gradient-to-br from-red-600 to-red-700 p-8 md:p-12 text-white space-y-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Why Contact Us?</h3>
                  <p className="text-red-100 leading-relaxed">
                    Our expert team is ready to help you find the right medications at the best prices.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold">Free Consultation</p>
                      <p className="text-sm text-red-100">Expert guidance at no cost</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold">Best Price Guarantee</p>
                      <p className="text-sm text-red-100">Save up to 60% on medications</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold">Quick Response</p>
                      <p className="text-sm text-red-100">We'll reach out within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold">Secure & Confidential</p>
                      <p className="text-sm text-red-100">Your information is protected</p>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-red-500">
                  <p className="text-sm text-red-100">
                    Response time: Within 24 hours
                  </p>
                  <p className="text-sm text-red-100 mt-2">
                    Available: Monday - Saturday, 9 AM - 6 PM IST
                  </p>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="md:col-span-3 p-8 md:p-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-gray-700 font-semibold">
                      Full Name <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="h-12 border-gray-300 focus:border-red-500 focus:ring-red-500"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700 font-semibold">
                      Email Address <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 border-gray-300 focus:border-red-500 focus:ring-red-500"
                    />
                  </div>

                  {/* Phone and Country */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700 font-semibold">
                        Phone Number <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+1 234 567 8900"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="h-12 border-gray-300 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-gray-700 font-semibold">
                        Country <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="country"
                        name="country"
                        type="text"
                        placeholder="United States"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="h-12 border-gray-300 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                  </div>

                  {/* Medication */}
                  <div className="space-y-2">
                    <Label htmlFor="medication" className="text-gray-700 font-semibold">
                      Medication Name(s)
                    </Label>
                    <Input
                      id="medication"
                      name="medication"
                      type="text"
                      placeholder="Enter medication names"
                      value={formData.medication}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-red-500 focus:ring-red-500"
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700 font-semibold">
                      Message / Requirements <span className="text-red-600">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your medication needs..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="border-gray-300 focus:border-red-500 focus:ring-red-500 resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>{contactFormData.ctaText}</span>
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </Button>

                  {/* Privacy Note */}
                  <p className="text-xs text-gray-600 text-center">
                    By submitting this form, you agree to our privacy policy. 
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
